/**
 * Caret position as a character offset into the editor's text content.
 *
 * Restoring an undo step reassigns innerHTML, which throws away every live Range. A text
 * offset survives that, because it is recomputed against the new DOM. Both functions count
 * only text-node characters, so they stay consistent with each other even though the offset
 * ignores element boundaries.
 */

export function getCaretOffset(root: HTMLElement): number {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return -1

  const range = selection.getRangeAt(0)
  if (!root.contains(range.endContainer)) return -1

  const measured = document.createRange()
  measured.selectNodeContents(root)
  measured.setEnd(range.endContainer, range.endOffset)
  return measured.toString().length
}

export function applyCaretOffset(root: HTMLElement, offset: number): void {
  if (offset < 0) return
  const selection = window.getSelection()
  if (!selection) return

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let remaining = offset
  let target: Text | null = null
  let targetOffset = 0

  let node = walker.nextNode()
  while (node) {
    const length = node.textContent?.length ?? 0
    if (remaining <= length) {
      target = node as Text
      targetOffset = remaining
      break
    }
    remaining -= length
    node = walker.nextNode()
  }

  const range = document.createRange()
  if (target) {
    range.setStart(target, targetOffset)
    range.collapse(true)
  } else {
    // Offset ran past the end of the restored content — land at the end rather than nowhere.
    range.selectNodeContents(root)
    range.collapse(false)
  }

  selection.removeAllRanges()
  selection.addRange(range)
}
